import React, { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import { Upload, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { api } from "./AuthContext";
import { toAbsUrl } from "../data/ApartmentDataContext";

export default function AdminGallery() {
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/gallery");
    setPhotos(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        await api.post("/admin/gallery", { url: data.url });
      }
      await load();
      toast.success(`${files.length} nuotrauka(-os) įkelta`);
    } catch (err) {
      toast.error("Nepavyko įkelti nuotraukos");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const del = async (id) => {
    if (!window.confirm("Ištrinti šią nuotrauką iš galerijos?")) return;
    try {
      await api.delete(`/admin/gallery/${id}`);
      await load();
      toast.success("Nuotrauka ištrinta");
    } catch (err) {
      toast.error("Nepavyko ištrinti");
    }
  };

  const move = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= photos.length) return;
    const arr = [...photos];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setPhotos(arr);
    try {
      await api.put("/admin/gallery/reorder", { order: arr.map((p) => p.id) });
    } catch (err) {
      toast.error("Nepavyko išsaugoti tvarkos");
      load();
    }
  };

  return (
    <div data-testid="gallery-panel" className="space-y-6">
      <h2 className="font-serif text-2xl text-[#1A1A1A]">Galerijos valdymas</h2>
      <p className="text-sm text-[#52525B]">Įkelkite nuotraukas, kurios bus rodomos svetainės „Galerija" skiltyje. Tvarką keiskite rodyklėmis.</p>

      <input ref={fileRef} data-testid="gallery-upload" type="file" accept="image/*" multiple onChange={onUpload} className="hidden" />
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
        data-testid="gallery-upload-btn"
        className="inline-flex items-center gap-2 bg-[#1A1A1A] text-white text-xs uppercase tracking-wide px-4 py-2 rounded hover:bg-[#2B2B2B] disabled:opacity-50"
      >
        <Upload size={14} /> {uploading ? "Keliama..." : "Įkelti nuotraukas"}
      </button>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {photos.map((p, i) => (
          <div key={p.id} data-testid={`gallery-item-${i}`} className="border border-[#E4E4E7] rounded-lg overflow-hidden bg-white">
            <div className="aspect-[4/3] bg-[#F5F3EE] flex items-center justify-center">
              <img src={toAbsUrl(p.url)} alt="" className="w-full h-full object-cover" />
            </div>
            <div className="flex items-center justify-between px-2 py-1.5 gap-1 bg-white">
              <button type="button" onClick={() => move(i, -1)} data-testid={`gallery-up-${i}`} className="text-[#52525B] hover:text-[#1A1A1A]" title="Aukštyn"><ArrowUp size={15} /></button>
              <button type="button" onClick={() => move(i, 1)} data-testid={`gallery-down-${i}`} className="text-[#52525B] hover:text-[#1A1A1A]" title="Žemyn"><ArrowDown size={15} /></button>
              <button type="button" onClick={() => del(p.id)} data-testid={`gallery-del-${i}`} className="text-red-500 hover:text-red-700 ml-auto" title="Ištrinti"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
        {photos.length === 0 && <p className="text-sm text-[#52525B] col-span-full py-6 text-center">Nuotraukų nėra</p>}
      </div>
    </div>
  );
}
